/* eslint-disable no-console */
/* eslint-disable no-restricted-syntax */

import { PublicCalendarEventSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";

/**
 * Cache parameters used to create a unique cache key for different filter sets.
 */
export interface CacheParams {
  slug: string | undefined;
  page_size: number;
  quantity_requested?: number;
  price_range?: string;
  event_id__in?: string | undefined;
  only_special_events: boolean;
  only_parents?: boolean;
  find_first_event: boolean;
  purchase_source_platform?: string;
}

/**
 * A simple time interval [start, end).
 */
interface TimeInterval {
  start: Date;
  end: Date;
}

/**
 * Internal representation of a cached event with pre-parsed dates.
 * This optimization avoids repeated date parsing on every query.
 */
interface CachedEventWithDates {
  event: PublicCalendarEventSerializer;
  startDate: Date;
  endDate: Date;
}

/**
 * In-memory cache for calendar events over date ranges.
 *
 * Key responsibilities:
 * 1. Track which time intervals you've already fetched (cachedIntervals).
 * 2. Store every fetched event exactly once (eventsById).
 * 3. Determine if a query range is fully cached.
 * 4. Merge new intervals into the existing set.
 * 5. Query cached events overlapping any requested range.
 *
 * Performance optimization:
 * - Dates are parsed once when events are added to the cache
 * - Subsequent queries use pre-parsed Date objects
 * - Eliminates repeated date string parsing overhead
 */
export class CalendarCache {
  /**
   * Map of event·id → event payload with pre-parsed dates.
   * Using a Map for O(1) dedupe + lookup.
   * Dates are parsed once at storage time for performance.
   */
  private eventsById = new Map<number, CachedEventWithDates>();

  /**
   * Sorted, non‑overlapping list of time intervals you've fetched.
   * Invariant: intervals are kept merged whenever possible.
   */
  public cachedIntervals: TimeInterval[] = [];

  /**
   * Returns true if [queryStart, queryEnd) is 100% covered by some single
   * cached interval. If so, you can skip any network calls.
   *
   * FIXED: Properly handles undefined queryEnd for unbounded queries.
   */
  public isRangeFullyCached(
    queryStart: Date,
    queryEnd: Date | undefined
  ): boolean {
    console.debug(
      `🧮 Checking if range is cached: [${queryStart.toISOString()}, ${
        queryEnd?.toISOString() || "(no end date specified)"
      }]`
    );
    console.debug(`     Cached intervals size:`, this.cachedIntervals.length);

    for (const interval of this.cachedIntervals) {
      console.debug(
        `       Checking interval: [${interval.start.toISOString()}, ${interval.end.toISOString()}]`
      );

      if (interval.start <= queryStart) {
        console.debug(`         ✓ Interval covers query start`);

        if (queryEnd) {
          console.debug(
            `         Bounded query: need end ${queryEnd.toISOString()} <= ${interval.end.toISOString()}`
          );

          if (queryEnd <= interval.end) {
            console.debug(`         ✅ BOUNDED MATCH: interval covers query`);
            return true;
          }
        } else {
          console.debug(
            `         ✅ UNBOUNDED MATCH: interval covers query with no end date`
          );
          // No queryEnd -> return true if interval covers the start
          return true;
        }
      } else {
        console.debug(`         ✗ Interval starts after query start`);
      }

      // Since intervals are sorted, once one interval's end > queryStart
      // we either found coverage or we know there's no perfect cover.
      if (interval.end > queryStart) {
        console.debug(`         🛑 Breaking: interval.end > queryStart`);
        break;
      }
    }

    console.debug(`         ❌ NO MATCH: range not cached`);
    return false;
  }

  /**
   * Merge a newly fetched interval into cachedIntervals, coalescing overlaps.
   * E.g. merging [Jan 5–Jan 15) into existing [Jan 1–Jan 10) yields [Jan 1–Jan 15).
   */
  private mergeNewInterval(newInterval: TimeInterval): void {
    // Combine and sort by start-time
    const all = [...this.cachedIntervals, newInterval].sort(
      (a, b) => a.start.getTime() - b.start.getTime()
    );

    const merged: TimeInterval[] = [];
    for (const interval of all) {
      if (merged.length === 0) {
        merged.push({ ...interval });
      } else {
        const last = merged[merged.length - 1];
        // If current interval overlaps or touches the last, extend it
        if (interval.start <= last.end) {
          last.end = new Date(
            Math.max(last.end.getTime(), interval.end.getTime())
          );
        } else {
          // Disjoint → becomes new segment
          merged.push({ ...interval });
        }
      }
    }

    this.cachedIntervals = merged;
  }

  /**
   * Add a batch of freshly fetched events plus the interval they cover.
   *
   * 1. Deduplicate & store each event in eventsById with pre-parsed dates.
   * 2. Merge the fetched interval into cachedIntervals.
   *
   * Performance: Dates are parsed once here, not on every query.
   */
  public addEventsBatch(
    fetchedEvents: PublicCalendarEventSerializer[],
    fetchedInterval: TimeInterval
  ): void {
    // 1) Store/update each event by its unique id with pre-parsed dates
    for (const evt of fetchedEvents) {
      this.eventsById.set(evt.id, {
        event: evt,
        startDate: new Date(evt.starts_on),
        endDate: new Date(evt.ends_on),
      });
    }
    // 2) Record that you've now covered this sub‑range
    this.mergeNewInterval(fetchedInterval);
  }

  /**
   * Return all cached events whose [start,end) overlaps the requested range.
   *
   * FIXED: Properly handles undefined queryEnd for unbounded queries.
   *
   * E.g. an event from Jan 9–Jan 11 overlaps a query Jan 10–Jan 12.
   *
   * Performance: Uses pre-parsed dates, avoiding repeated date string parsing.
   */
  public queryEventsInRange(
    queryStart: Date,
    queryEnd: Date | undefined
  ): PublicCalendarEventSerializer[] {
    const result: PublicCalendarEventSerializer[] = [];

    for (const cachedItem of this.eventsById.values()) {
      // Use pre-parsed dates for performance (no date parsing here!)
      const { event, startDate: eventStart, endDate: eventEnd } = cachedItem;

      // Overlap condition: eventEnd > queryStart && eventStart < queryEnd
      if (eventEnd > queryStart) {
        if (queryEnd) {
          if (eventStart < queryEnd) {
            result.push(event);
          }
        } else {
          // No query end -> push only if first condition passed
          result.push(event);
        }
      }
    }

    return result;
  }
}
